import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUsers } from "../../redux/apis/normalUser/userListSlice";

const PatientsViewWithAccessControl = ({
  groupedByPatient = {},
  currentUserEmail,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading: authLoading, isAuthenticated } = useSelector(
    (state) => state.auth
  );

  const { users, loading: usersLoading } = useSelector(
    (state) => state.userList
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [professionFilter, setProfessionFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ============================================================
  // AUTH REDIRECT
  // ============================================================

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // ============================================================
  // FETCH USERS
  // ============================================================

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // ============================================================
  // SAFE USERS NORMALIZATION
  // ============================================================

  const safeUsers = useMemo(() => {
    if (Array.isArray(users)) return users;
    if (Array.isArray(users?.users)) return users.users;
    if (Array.isArray(users?.data)) return users.data;
    if (users && typeof users === "object") return [users];
    return [];
  }, [users]);

  // ============================================================
  // USER MAP
  // ============================================================

  const userMap = useMemo(() => {
    const map = new Map();

    safeUsers.forEach((u) => {
      if (u?._id) {
        map.set(u._id, u);
      }
    });

    return map;
  }, [safeUsers]);

  // ============================================================
  // NORMALIZE PATIENT GROUPS
  // ============================================================

  const safeGroups = useMemo(() => {
    const result = [];

    Object.values(groupedByPatient || {}).forEach((group) => {
      const patientId =
        group?.patient?.id || group?.patient?._id;

      if (!patientId) return;

      const user = userMap.get(patientId);

      result.push({
        patient: {
          _id: patientId,
          fullName:
            user?.name ||
            group?.patient?.name ||
            "Unknown",
          email:
            user?.email ||
            group?.patient?.email ||
            "",
          phone:
            user?.contact ||
            group?.patient?.phone ||
            "",
          profession:
            user?.role ||
            "user",
        },

        appointments: Array.isArray(
          group?.appointments
        )
          ? group.appointments
          : [],
      });
    });

    return result;
  }, [groupedByPatient, userMap]);

  // ============================================================
  // FILTER LOGIC
  // ============================================================

  const filteredPatients = safeGroups.filter(
    ({ patient, appointments }) => {
      const search = searchTerm
        .trim()
        .toLowerCase();

      const matchesSearch =
        !search ||
        patient.fullName
          ?.toLowerCase()
          .includes(search) ||
        patient.email
          ?.toLowerCase()
          .includes(search) ||
        patient.phone
          ?.toLowerCase()
          .includes(search);

      const matchesProfession =
        !professionFilter ||
        patient.profession === professionFilter;

      const matchesDate =
        !startDate && !endDate
          ? true
          : appointments.some((appt) => {
              if (!appt?.date) return false;

              const d = new Date(appt.date);

              if (Number.isNaN(d.getTime())) {
                return false;
              }

              const start = startDate
                ? new Date(`${startDate}T00:00:00`)
                : null;

              const end = endDate
                ? new Date(`${endDate}T23:59:59`)
                : null;

              return (
                (!start || d >= start) &&
                (!end || d <= end)
              );
            });

      return (
        matchesSearch &&
        matchesProfession &&
        matchesDate
      );
    }
  );

  // ============================================================
  // UNIQUE PROFESSIONS
  // ============================================================

  const uniqueProfessions = [
    ...new Set(
      safeGroups
        .map((p) => p?.patient?.profession)
        .filter(Boolean)
    ),
  ];

  // ============================================================
  // LOADING
  // ============================================================

  if (usersLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-2xl bg-white px-8 py-6 shadow-md">
          <div
            className="
              mx-auto
              h-8
              w-8
              animate-spin
              rounded-full
              border-4
              border-gray-200
              border-t-[#0ea5e9]
            "
          />

          <p className="mt-3 text-center text-sm font-semibold text-gray-600">
            Loading patients...
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div
        className="
          mb-6
          overflow-hidden
          rounded-3xl
          p-5
          text-white
          shadow-lg
          sm:p-6
        "
        style={{
          backgroundImage:
            "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
        }}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">
              Patient Management
            </p>

            <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">
              Patients
            </h1>

            <p className="mt-1 text-sm text-blue-100">
              View and manage your patient appointments
              and details.
            </p>
          </div>

          <div
            className="
              flex
              h-14
              w-14
              items-center
              justify-center
              rounded-2xl
              border
              border-white/10
              bg-white/10
              text-2xl
              shadow-inner
              backdrop-blur-sm
            "
          >
            👥
          </div>

        </div>
      </div>

      {/* ========================================================
          FILTERS
      ======================================================== */}

      <div
        className="
          mb-6
          rounded-2xl
          border
          border-gray-100
          bg-white
          p-4
          shadow-sm
          sm:p-5
        "
      >

        <div className="mb-4">
          <h2 className="text-sm font-bold text-gray-800">
            Patient Filters
          </h2>

          <p className="mt-1 text-xs text-gray-400">
            Search patients or filter appointments by
            profession and date.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">

          {/* SEARCH */}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">
              Search Patient
            </label>

            <input
              type="text"
              placeholder="Name, email or phone..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              className="
                w-full
                rounded-xl
                border
                border-gray-200
                bg-gray-50
                px-3
                py-2.5
                text-sm
                text-gray-700
                outline-none
                transition
                placeholder:text-gray-400
                focus:border-sky-500
                focus:bg-white
                focus:ring-2
                focus:ring-sky-100
              "
            />
          </div>

          {/* PROFESSION */}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">
              Profession
            </label>

            <select
              value={professionFilter}
              onChange={(e) =>
                setProfessionFilter(e.target.value)
              }
              className="
                w-full
                rounded-xl
                border
                border-gray-200
                bg-gray-50
                px-3
                py-2.5
                text-sm
                text-gray-700
                outline-none
                transition
                focus:border-sky-500
                focus:bg-white
                focus:ring-2
                focus:ring-sky-100
              "
            >
              <option value="">
                All Professions
              </option>

              {uniqueProfessions.map(
                (prof, idx) => (
                  <option
                    key={idx}
                    value={prof}
                  >
                    {prof}
                  </option>
                )
              )}
            </select>
          </div>

          {/* START DATE */}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">
              Start Date
            </label>

            <input
              type="date"
              value={startDate}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
              className="
                w-full
                rounded-xl
                border
                border-gray-200
                bg-gray-50
                px-3
                py-2.5
                text-sm
                text-gray-700
                outline-none
                transition
                focus:border-sky-500
                focus:bg-white
                focus:ring-2
                focus:ring-sky-100
              "
            />
          </div>

          {/* END DATE */}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">
              End Date
            </label>

            <input
              type="date"
              value={endDate}
              onChange={(e) =>
                setEndDate(e.target.value)
              }
              className="
                w-full
                rounded-xl
                border
                border-gray-200
                bg-gray-50
                px-3
                py-2.5
                text-sm
                text-gray-700
                outline-none
                transition
                focus:border-sky-500
                focus:bg-white
                focus:ring-2
                focus:ring-sky-100
              "
            />
          </div>

        </div>
      </div>

      {/* ========================================================
          PATIENT COUNT
      ======================================================== */}

      <div className="mb-4 flex items-center justify-between">

        <div>
          <h2 className="text-lg font-bold text-gray-800">
            Patient List
          </h2>

          <p className="mt-1 text-xs text-gray-400">
            {filteredPatients.length} patient
            {filteredPatients.length !== 1
              ? "s"
              : ""}{" "}
            found
          </p>
        </div>

      </div>

      {/* ========================================================
          PATIENT LIST
      ======================================================== */}

      {filteredPatients.length === 0 ? (

        <div
          className="
            rounded-2xl
            border
            border-gray-100
            bg-white
            p-10
            text-center
            shadow-sm
          "
        >
          <div
            className="
              mx-auto
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-2xl
              bg-blue-50
              text-2xl
            "
          >
            👥
          </div>

          <p className="mt-4 text-sm font-bold text-gray-700">
            No matching patients found
          </p>

          <p className="mt-1 text-xs text-gray-400">
            Try changing your search or filter criteria.
          </p>
        </div>

      ) : (

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">

          {filteredPatients.map(
            ({ patient, appointments }) => (

              <div
                key={patient._id}
                className="
                  group
                  overflow-hidden
                  rounded-2xl
                  border
                  border-gray-100
                  bg-white
                  shadow-sm
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:shadow-lg
                "
              >

                {/* PATIENT HEADER */}

                <div
                  className="p-5 text-white"
                  style={{
                    backgroundImage:
                      "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
                  }}
                >

                  <div className="flex items-center gap-3">

                    <div
                      className="
                        flex
                        h-12
                        w-12
                        shrink-0
                        items-center
                        justify-center
                        rounded-2xl
                        border
                        border-white/10
                        bg-white/10
                        text-lg
                        font-bold
                        text-sky-200
                        backdrop-blur-sm
                      "
                    >
                      {patient.fullName
                        ?.charAt(0)
                        ?.toUpperCase() || "P"}
                    </div>

                    <div className="min-w-0">

                      <h2 className="truncate text-lg font-bold">
                        {patient.fullName}
                      </h2>

                      <p className="mt-0.5 truncate text-xs text-blue-100">
                        {patient.profession}
                      </p>

                    </div>

                  </div>

                </div>

                {/* PATIENT DETAILS */}

                <div className="p-5">

                  <div className="space-y-3">

                    {/* EMAIL */}

                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Email
                      </p>

                      <p className="mt-0.5 break-all text-sm font-medium text-gray-700">
                        {patient.email || "N/A"}
                      </p>
                    </div>

                    {/* PHONE */}

                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Phone
                      </p>

                      <p className="mt-0.5 text-sm font-medium text-gray-700">
                        {patient.phone || "N/A"}
                      </p>
                    </div>

                  </div>

                  {/* TOTAL APPOINTMENTS */}

                  <div
                    className="
                      mt-5
                      rounded-xl
                      border
                      border-sky-100
                      bg-gradient-to-r
                      from-sky-50
                      to-blue-50
                      p-3
                    "
                  >
                    <div className="flex items-center justify-between">

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600">
                          Total Appointments
                        </p>

                        <p className="mt-1 text-2xl font-extrabold text-[#1a3a5c]">
                          {appointments.length}
                        </p>
                      </div>

                      <div
                        className="
                          flex
                          h-10
                          w-10
                          items-center
                          justify-center
                          rounded-xl
                          text-white
                          shadow-sm
                        "
                        style={{
                          backgroundImage:
                            "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                        }}
                      >
                        📅
                      </div>

                    </div>
                  </div>

                  {/* APPOINTMENTS */}

                  {appointments.length > 0 && (

                    <div className="mt-5">

                      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500">
                        Appointment History
                      </p>

                      <div className="max-h-64 space-y-2 overflow-y-auto pr-1">

                        {appointments.map(
                          (appt, idx) => {

                            const rawStatus =
                              String(
                                appt?.status ||
                                  "pending"
                              )
                                .trim()
                                .toLowerCase();

                            const isCompleted =
                              rawStatus ===
                              "completed";

                            const isCancelled =
                              rawStatus ===
                                "cancelled" ||
                              rawStatus ===
                                "canceled" ||
                              rawStatus ===
                                "declined" ||
                              rawStatus ===
                                "rejected";

                            const isConfirmed =
                              rawStatus ===
                              "confirmed";

                            return (
                              <div
                                key={idx}
                                className="
                                  rounded-xl
                                  border
                                  border-gray-100
                                  bg-gray-50
                                  p-3
                                  transition
                                  hover:border-sky-100
                                  hover:bg-sky-50/40
                                "
                              >

                                <div className="flex items-center justify-between gap-3">

                                  <div>
                                    <p className="text-xs font-bold text-gray-700">
                                      {appt?.date
                                        ? new Date(
                                            appt.date
                                          ).toLocaleString(
                                            "en-IN",
                                            {
                                              day: "2-digit",
                                              month:
                                                "short",
                                              year:
                                                "numeric",
                                              hour:
                                                "2-digit",
                                              minute:
                                                "2-digit",
                                            }
                                          )
                                        : "N/A"}
                                    </p>
                                  </div>

                                  <span
                                    className={`
                                      shrink-0
                                      rounded-full
                                      px-2.5
                                      py-1
                                      text-[10px]
                                      font-bold
                                      ${
                                        isCompleted
                                          ? "bg-cyan-50 text-cyan-700"
                                          : isCancelled
                                          ? "bg-red-50 text-red-700"
                                          : isConfirmed
                                          ? "bg-green-50 text-green-700"
                                          : "bg-yellow-50 text-yellow-700"
                                      }
                                    `}
                                  >
                                    {appt?.status ||
                                      "Pending"}
                                  </span>

                                </div>

                                <p className="mt-2 text-xs text-gray-500">
                                  <span className="font-semibold text-gray-600">
                                    Notes:
                                  </span>{" "}
                                  {appt?.notes ||
                                    "N/A"}
                                </p>

                              </div>
                            );
                          }
                        )}

                      </div>

                    </div>

                  )}

                </div>

              </div>
            )
          )}

        </div>
      )}

    </div>
  );
};

export default PatientsViewWithAccessControl;